import { Group } from '@mantine/core';
import { DeviceListAllEvidenceFieldsResponse } from 'api/actions/device-list-all-evidence-fields/device-list-all-evidence-fields-response';
import { useApi } from 'api/api-context';
import {
  DataSelectInheritedProps,
  DataSelect,
  DataMultiSelectInheritedProps,
  DataMultiSelect,
} from 'components/selects/DataSelect';
import P4Medium from 'components/typography/P4Medium';
import { forwardRef, useCallback } from 'react';

export type EvidenceFieldApplicability = 'devices' | 'people' | 'both';

interface EvidenceFieldApplicabilityProps {
  applicability: EvidenceFieldApplicability;
}

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  item: DeviceListAllEvidenceFieldsResponse[number];
}

const DeviceTypeItem = forwardRef<HTMLDivElement, ItemProps>(({ item, ...others }: ItemProps, ref) => {
  return (
    <div ref={ref} {...others}>
      <Group noWrap spacing={4} align="baseline">
        <P4Medium c="gray.4">(#{item.evidenceFieldId})</P4Medium>
        {item.fieldName}
      </Group>
    </div>
  );
});

/**
 * Hook to fetch evidence fields for the declared consumer context.
 */
function useFetchEvidenceFields(applicability: EvidenceFieldApplicability) {
  const { getAction } = useApi();

  const fetchEvidenceFields = useCallback(async () => {
    const fetchEvidenceFields = getAction('DeviceListAllEvidenceFields');
    const fields = await fetchEvidenceFields();

    return fields
      .filter(
        (field) =>
          applicability === 'both' ||
          (applicability === 'devices' && field.appliesToDevices) ||
          (applicability === 'people' && field.appliesToPeople)
      )
      .map((field) => ({
        ...field,
        _value: field.evidenceFieldId,
        _label: `(#${field.evidenceFieldId}) ${field.fieldName}`,
      }));
  }, [applicability, getAction]);

  return fetchEvidenceFields;
}

/**
 * Select input for evidence fields.
 */
export function EvidenceFieldSelect({
  applicability,
  ...props
}: DataSelectInheritedProps & EvidenceFieldApplicabilityProps) {
  const fetchEvidenceFields = useFetchEvidenceFields(applicability);

  return (
    <DataSelect
      action={fetchEvidenceFields}
      labelProp="_label"
      valueProp="_value"
      itemComponent={DeviceTypeItem}
      {...props}
    />
  );
}

/**
 * Select input for evidence fields (multiple).
 */
export function EvidenceFieldMultiSelect({
  applicability,
  ...props
}: DataMultiSelectInheritedProps & EvidenceFieldApplicabilityProps) {
  const fetchEvidenceFields = useFetchEvidenceFields(applicability);

  return (
    <DataMultiSelect
      action={fetchEvidenceFields}
      labelProp="_label"
      valueProp="_value"
      itemComponent={DeviceTypeItem}
      {...props}
    />
  );
}
